import { useCallback, useEffect } from 'react'
import { useBoolean } from './useBoolean'
import type { UseCounterOptions } from './useCounter'
import { useCounter } from './useCounter'
import { useInterval } from './useInterval'

export interface UseCountdownOptions extends UseCounterOptions {
  seconds?: number
  interval?: number
  isIncrement?: boolean
  lowerBoundaryLimit?: number
  upperBoundaryLimit?: number
}

export function useCountdown(options: UseCountdownOptions = {}) {
  const {
    seconds = 30,
    interval = 1000,
    lowerBoundaryLimit = -Infinity,
    upperBoundaryLimit = Infinity,
    isIncrement = false
  } = options

  const [count, counterActions] = useCounter(seconds, {
    incrementCallback: options.incrementCallback,
    decrementCallback: options.decrementCallback
  })
  const [running, intervalActions] = useBoolean(false)

  const reset = useCallback(() => {
    intervalActions.setFalse()
    counterActions.reset()
  }, [intervalActions, counterActions])

  useInterval(
    isIncrement ? counterActions.increment : counterActions.decrement,
    running ? interval : null
  )

  useEffect(() => {
    if (!running) return
    if (
      (isIncrement && count >= upperBoundaryLimit) ||
      (!isIncrement && count <= lowerBoundaryLimit)
    ) {
      reset()
    }
  }, [count, running])

  return [
    count,
    {
      start: intervalActions.setTrue,
      stop: intervalActions.setFalse,
      running,
      reset
    }
  ] as const
}
